// @flow
// What the record's editor tells Bugsnag when a part of it fails, once a session each (what failed is the same for
// every record): its kind, the error's class and where it failed. Never the error's message nor anything it carries:
// ProseMirror's and Tiptap's quote the document they were given, the patient's record.
import Bugsnag from '@bugsnag/js';
import { stackFrames, markTextFreeReport } from '@helpers/bugsnagPrivacy';

const FAILURES = {
  load: { context: 'RichTextEditorLoadFailed', message: 'The record editor\'s code didn\'t load' },
  render: { context: 'RichTextEditorFailed', message: 'The record editor broke while it showed' },
  read: { context: 'RichTextEditorReadFailed', message: 'The record\'s text couldn\'t be read for the editor' },
  content: { context: 'RichTextEditorContentRefused', message: 'The record editor\'s schema refused content' },
};
export type EditorFailure = 'load' | 'render' | 'read' | 'content';

const reported: { [EditorFailure]: boolean } = {};

// The error's class (RangeError, ChunkLoadError, RecordDocumentError…), when it is only a name (here and for the
// editor's console guard)
export const errorClass = (error: any): string => {
  let name = '';
  try {
    name = error && typeof error.name === 'string' ? error.name : '';
  } catch (e) {
    // No class to tell
  }
  return /^[A-Za-z_$][A-Za-z0-9_$]{0,63}$/.test(name) ? name : 'Error';
};

// The report's error: the class, the kind's own words, and the failed error's frames (V8 starts its stack with the
// error's line, JavaScriptCore and Gecko with the frames). Marked text-free: Bugsnag's filter leaves its words as they
// are (bugsnagPrivacy).
export const reportedError = (kind: EditorFailure, error: any): Error => {
  const safe = new Error(FAILURES[kind].message);
  safe.name = errorClass(error);
  let frames = [];
  try {
    frames = stackFrames(error && error.stack);
  } catch (e) {
    // Where it failed is unknown: this error's own stack
  }
  if (frames.length) safe.stack = /^\s*at /.test(frames[0]) ? [`${safe.name}: ${safe.message}`, ...frames].join('\n') : frames.join('\n');
  markTextFreeReport(safe);
  return safe;
};

export const reportEditorFailure = (kind: EditorFailure, error: any) => {
  if (reported[kind]) return;
  reported[kind] = true;
  Bugsnag.notify(reportedError(kind, error), (event) => {
    event.context = FAILURES[kind].context; // eslint-disable-line no-param-reassign
  });
};
